import React, { useState, useEffect } from 'react';
import { Box, TextField, Button, useTheme, Snackbar } from '@mui/material';
import { DataGridPremium, GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium';
import { adminGetAllUsers, adminCreateUser, CreateUserParams } from '../../../admin.users.api';
import { UserData } from '../../../../../utils';
import { styled } from '@mui/system';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import CreateUserDialog from '../../../components/CreateUserDialog';
import UserSearchDialog from '../components/UserSearchDialog';
import EditUserDialog from '../components/EditUserDialog';

const ResponsiveFormItem = styled(Box)(({ theme }) => ({
    width: 'auto',
    flexGrow: 0,
    margin: theme.spacing(1),
}));

const ResponsiveForm = styled('form')(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    margin: theme.spacing(-1),
}));

const UsersList = () => {
    const theme = useTheme();
    const [users, setUsers] = useState<UserData[]>([]);
    const [nameFilter, setNameFilter] = useState('');
    const [emailFilter, setEmailFilter] = useState('');
    const [userNameFilter, setUserNameFilter] = useState('');
    const [snackMessage, setSnackMessage] = useState('');
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 25,
    });
    const [createUserDialogOpen, setCreateUserDialogOpen] = useState(false);
    const [userSearchDialogOpen, setUserSearchDialogOpen] = useState(false);
    const [editingUser, setEditingUser] = useState<UserData | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const handleSearch = async (event?: React.FormEvent) => {
        if (event) {
            event.preventDefault();
        }
        setIsLoading(true);
        try {
            const response = await adminGetAllUsers(
                {
                    name: nameFilter,
                    email: emailFilter,
                    userName: userNameFilter,
                },
                paginationModel.pageSize,
            );
            setUsers(response.data.data);
        } catch (error) {
            console.error('Failed to fetch users:', error);
            setSnackMessage('Failed to fetch users');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreateUser = async (userData: CreateUserParams) => {
        try {
            await adminCreateUser(userData);
            setSnackMessage(
                `User ${userData.first_name} ${userData.last_name} created successfully`,
            );
            setCreateUserDialogOpen(false);
            await handleSearch(); // Refresh the user list
        } catch (error: any) {
            console.error('Failed to create user:', error);
            setSnackMessage(
                `Failed to create user: ${error.response?.data?.message || error.message}`,
            );
        }
    };

    // Load users on component mount
    useEffect(() => {
        handleSearch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const columns: GridColDef<UserData>[] = [
        {
            field: 'uuid',
            headerName: 'UUID',
            flex: 1,
            minWidth: 280,
        },
        {
            field: 'name',
            headerName: 'Name',
            flex: 1,
            minWidth: 200,
            renderCell: (params: GridRenderCellParams<UserData>) => {
                return `${params.row.first_name} ${params.row.last_name}`;
            },
        },
        {
            field: 'user_name',
            headerName: 'Username',
            flex: 1,
            minWidth: 150,
        },
        {
            field: 'email',
            headerName: 'Email',
            flex: 1.5,
            minWidth: 200,
        },
        {
            field: 'phone',
            headerName: 'Phone',
            flex: 1,
            minWidth: 150,
            renderCell: (params: GridRenderCellParams<UserData>) => {
                return params.row.phone || '';
            },
        },
    ];

    return (
        <Box sx={{ margin: theme.spacing(0, 2) }}>
            <Snackbar
                open={!!snackMessage}
                message={snackMessage}
                autoHideDuration={6000}
                onClose={() => setSnackMessage('')}
            />
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: theme.spacing(2),
                }}
            >
                <h2>User Management</h2>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<SearchIcon />}
                        onClick={() => setUserSearchDialogOpen(true)}
                    >
                        Edit User
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={() => setCreateUserDialogOpen(true)}
                    >
                        Create User
                    </Button>
                </Box>
            </Box>
            <section style={{ marginBottom: theme.spacing(2) }}>
                <h4>Search Users</h4>
                <ResponsiveForm onSubmit={handleSearch} noValidate>
                    <ResponsiveFormItem>
                        <TextField
                            label="Search by Name"
                            value={nameFilter}
                            onChange={(e) => setNameFilter(e.target.value)}
                            size="small"
                            sx={{ width: '250px' }}
                        />
                    </ResponsiveFormItem>
                    <ResponsiveFormItem>
                        <TextField
                            label="Search by Email"
                            value={emailFilter}
                            onChange={(e) => setEmailFilter(e.target.value)}
                            size="small"
                            sx={{ width: '250px' }}
                        />
                    </ResponsiveFormItem>
                    <ResponsiveFormItem>
                        <TextField
                            label="Search by Username"
                            value={userNameFilter}
                            onChange={(e) => setUserNameFilter(e.target.value)}
                            size="small"
                            sx={{ width: '250px' }}
                        />
                    </ResponsiveFormItem>
                    <ResponsiveFormItem>
                        <Button
                            type="submit"
                            variant="contained"
                            size="medium"
                            sx={{ height: '40px' }}
                            disabled={isLoading}
                        >
                            {isLoading ? 'Searching...' : 'Search Users'}
                        </Button>
                    </ResponsiveFormItem>
                </ResponsiveForm>
            </section>

            <Box width="98%" maxHeight="600px" overflow="auto">
                <DataGridPremium<UserData>
                    rows={users}
                    columns={columns}
                    getRowId={(row) => row.uuid}
                    loading={isLoading}
                    pagination
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    pageSizeOptions={[10, 25, 50, 100]}
                    disableColumnMenu
                    style={{ height: '624px', marginBottom: '50px' }}
                    sx={{
                        '& .MuiDataGrid-cell:focus': {
                            outline: 'none',
                        },
                        '& .MuiDataGrid-cell:focus-within': {
                            outline: 'none',
                        },
                        '& .MuiDataGrid-cell': {
                            fontSize: '0.75rem',
                        },
                        '& .MuiDataGrid-columnHeader': {
                            fontSize: '0.75rem',
                        },
                        '& .MuiDataGrid-columnHeaderTitle': {
                            fontSize: '0.75rem',
                            fontWeight: 600,
                        },
                        '& .MuiDataGrid-footerContainer': {
                            fontSize: '0.75rem',
                        },
                    }}
                />
            </Box>
            <CreateUserDialog
                open={createUserDialogOpen}
                onClose={() => setCreateUserDialogOpen(false)}
                onSubmit={handleCreateUser}
            />
            <UserSearchDialog
                open={userSearchDialogOpen}
                onClose={() => setUserSearchDialogOpen(false)}
                onSelect={(user) => {
                    setUserSearchDialogOpen(false);
                    setEditingUser(user);
                }}
            />
            <EditUserDialog
                open={!!editingUser}
                user={editingUser}
                onClose={() => setEditingUser(null)}
                onSaved={() => {
                    setSnackMessage('User updated successfully');
                    handleSearch();
                }}
            />
        </Box>
    );
};

export default UsersList;
